import Vue from 'vue'
import conf from '../config'
import { getIdToken } from '../misc/auth'

const apiBase = conf.LLM_COMPARISON_API_ENDPOINT || conf.API_URL || ''
const apiPrefix = '/api/llm-comparisons'

function normalizeBase(url) {
  if (!url) return ''
  return String(url).replace(/\/+$/, '')
}

const base = normalizeBase(apiBase)

function buildUrl(path) {
  if (!base) return ''
  return `${base}${apiPrefix}${path}`
}

function authConfig(extra = {}) {
  const token = getIdToken && getIdToken()
  if (!token) {
    return extra
  }
  return {
    ...extra,
    headers: {
      ...(extra.headers || {}),
      Authorization: `Bearer ${token}`
    }
  }
}

export default {
  async createComparison(formData) {
    let file, modelA, modelB, metadata
    
    // Extract data from FormData or Object
    if (typeof FormData !== 'undefined' && formData instanceof FormData) {
      file = formData.get('file') || formData.get('pdf')
      modelA = formData.get('modelA')
      modelB = formData.get('modelB')
      const metaStr = formData.get('metadata')
      if (metaStr && typeof metaStr === 'string') {
        try {
          metadata = JSON.parse(metaStr)
        } catch (e) { /* ignore */ }
      } else {
        metadata = metaStr
      }
    } else {
       ({ file, modelA, modelB, metadata } = formData)
    }

    if (!file) {
      return Promise.reject(new Error('File is required'))
    }

    // 1. Initialize Job and Get Presigned URL
    const initPayload = {
      modelA,
      modelB,
      filename: file.name,
      metadata
    }

    // Use standard post, it returns a Promise
    const initResp = await Vue.prototype.$http.post(buildUrl(''), initPayload, authConfig())
    const body = initResp.data || initResp.body
    const { jobId, uploadUrl } = body

    if (!uploadUrl) {
      throw new Error('Server did not return upload URL')
    }

    // 2. Upload to S3 directly
    // Use native fetch to avoid global interceptors adding Authorization headers to S3 request
    const s3Resp = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': 'application/pdf'
      }
    })

    if (!s3Resp.ok) {
      throw new Error(`Upload failed: ${s3Resp.statusText}`)
    }

    // Return compatible response structure
    return { 
      data: { jobId, status: 'queued' }, 
      body: { jobId, status: 'queued' } 
    }
  },
  listComparisons(params = {}) {
    return Vue.prototype.$http.get(buildUrl(''), authConfig({ params }))
  },
  getComparison(jobId) {
    return Vue.prototype.$http.get(buildUrl(`/${encodeURIComponent(jobId)}`), authConfig())
  },

  getAvailableModels() {
    return Vue.prototype.$http.get(buildUrl('/models'), authConfig())
  },

  async pollJobStatus(jobId, onUpdate, opts = {}) {
    const maxMinutes = typeof opts.maxMinutes === 'number' ? opts.maxMinutes : 10
    const baseDelayMs = typeof opts.baseDelayMs === 'number' ? opts.baseDelayMs : 10000
    const maxDelayMs = typeof opts.maxDelayMs === 'number' ? opts.maxDelayMs : 50000

    const deadline = Date.now() + maxMinutes * 60 * 1000
    let attempt = 0

    while (Date.now() < deadline) {
      // eslint-disable-next-line no-await-in-loop
      const resp = await this.getComparison(jobId)
      const job = resp && resp.data ? resp.data : null
      if (typeof onUpdate === 'function') onUpdate(job)

      const status = job && job.status
      if (['complete', 'partial', 'failed'].includes(status)) return job

      const delay = Math.min(baseDelayMs * Math.floor(attempt / 2 + 1), maxDelayMs)
      attempt += 1
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => setTimeout(resolve, delay))
    }

    throw new Error('Polling timeout')
  },

  formatError(e) {
    const status = e && e.response && e.response.status
    const backendMsg = e && e.response && e.response.data && (e.response.data.error || e.response.data.m)

    if (status === 400) return backendMsg || 'Bad request'
    if (status === 401) return 'Unauthorized. Please sign in again.'
    if (status === 403) return 'Forbidden. You do not have access.'
    if (status >= 500) return 'Server error. Please try again later.'

    return backendMsg || (e && e.message) || 'Request failed'
  },

  // =========================================================================
  // Scoring API Methods
  // =========================================================================

  /**
   * Get score for a specific job and model combination.
   * @param {string} jobId - The job ID
   * @param {string} model - The model name
   * @returns {Promise} Response containing the score data
   */
  getScore(jobId, model) {
    const url = buildUrl(`/${encodeURIComponent(jobId)}/scores/${encodeURIComponent(model)}`)
    return Vue.prototype.$http.get(url, authConfig())
  },

  /**
   * Save (upsert) score for a specific job and model combination.
   * @param {string} jobId - The job ID
   * @param {string} model - The model name
   * @param {Object} scoreData - The score data containing core_metrics, optional_attributes, etc.
   * @returns {Promise} Response indicating success/failure
   */
  saveScore(jobId, model, scoreData) {
    const url = buildUrl('/scores')
    const payload = {
      job_id: jobId,
      model: model,
      ...scoreData
    }
    return Vue.prototype.$http.put(url, payload, authConfig())
  },

  /**
   * Delete score for a specific job and model combination.
   * @param {string} jobId - The job ID
   * @param {string} model - The model name
   * @returns {Promise} Response indicating success/failure
   */
  deleteScore(jobId, model) {
    const url = buildUrl('/scores')
    const payload = {
      job_id: jobId,
      model: model
    }
    return Vue.prototype.$http.delete(url, authConfig({ data: payload }))
  }
}
